/**
 * Aggregate and deduplicate topics from a list of documents.
 * Returns sorted unique topic strings.
 */

// BUG-RETEST-F (#3445): client-side belt-and-braces placeholder filter.
// Backend (content_safety.py) is the authoritative filter; this is a second
// safety net for tokens that reach the frontend despite the backend filter.
const PLACEHOLDER_EXACT = new Set([
  'file', 'model',
  'fullmodelcollapsedstl', 'plainobj', 'plainmtl', 'isopng',
  'newmtl', 'wavefront', 'autodesk', 'fusion', 'manifest',
  'header', 'vertices', 'triangles', 'normals',
  'bulkstream', 'metastream', 'parameshgeometry', 'fusionassetname',
  // Story #3477 Streams 5+6: generic English stop-words surfacing as hollow topics
  'concept', 'design', 'device', 'generated', 'need', 'please',
  'total', 'years', 'approach', 'application', 'process', 'method',
  'solution', 'system', 'type', 'use', 'used', 'using',
  'based', 'related', 'including',
])
const PURE_NUMERIC = /^\d+$/
const COORD_STYLE = /^[xyz]\d{4,}$/i
const HEX_STRING = /^[0-9a-f]{6,}$/i

/** Maps lowercased variant/alias forms to a canonical display name. */
export const TOPIC_ALIAS_MAP: Record<string, string> = {
  'cad': 'Computer-Aided Design',
  'computer-aided design (cad)': 'Computer-Aided Design',
  'computer aided design (cad)': 'Computer-Aided Design',
  'computer aided design': 'Computer-Aided Design',
  'ai': 'Artificial Intelligence',
  'ml': 'Machine Learning',
  'machine learning (ml)': 'Machine Learning',
  'nlp': 'Natural Language Processing',
  'natural language processing (nlp)': 'Natural Language Processing',
  'iot': 'Internet of Things',
  'internet of things (iot)': 'Internet of Things',
  'ar': 'Augmented Reality',
  'vr': 'Virtual Reality',
  'xr': 'Extended Reality',
  'ev': 'Electric Vehicles',
}

/** Maps raw taxonomy strings (lowercase) to shorter human-readable display names. */
export const TAXONOMY_LABEL_MAP: Record<string, string> = {
  'energy & fuels': 'Energy',
  'energy and fuels': 'Energy',
  'electrical & electronic engineering': 'Electrical Eng.',
  'electrical and electronic engineering': 'Electrical Eng.',
  'engineering, electrical & electronic': 'Electrical Eng.',
  'engineering, electrical and electronic': 'Electrical Eng.',
  'environmental sciences & ecology': 'Environmental Sci.',
  'environmental sciences and ecology': 'Environmental Sci.',
  'computer science, artificial intelligence': 'AI',
  'computer science, information systems': 'Information Systems',
  'computer science, interdisciplinary applications': 'Computer Science',
  'computer science, theory & methods': 'CS Theory',
  'computer science, theory and methods': 'CS Theory',
  'engineering, multidisciplinary': 'Engineering',
  'engineering, chemical': 'Chemical Eng.',
  'engineering, mechanical': 'Mechanical Eng.',
  'engineering, civil': 'Civil Eng.',
  'materials science, multidisciplinary': 'Materials Science',
  'chemistry, multidisciplinary': 'Chemistry',
  'physics, applied': 'Applied Physics',
  'physics, multidisciplinary': 'Physics',
  'telecommunications': 'Telecom',
  'automation & control systems': 'Automation',
  'automation and control systems': 'Automation',
  'thermodynamics': 'Thermodynamics',
}

/**
 * Return a shorter display label for a raw taxonomy string.
 * Falls back to the raw string if no mapping exists.
 */
export function formatTopicLabel(raw: string): string {
  return TAXONOMY_LABEL_MAP[raw.toLowerCase()] ?? raw
}

/** Strip trailing parentheticals e.g. "Computer-Aided Design (CAD)" → "Computer-Aided Design". */
function stripParenthetical(topic: string): string {
  return topic.replace(/\s*\([^)]+\)\s*$/, '').trim()
}

/**
 * Normalise a raw topic string: strip parentheticals, then resolve aliases.
 * Returns the canonical form ready for deduplication.
 */
export function normalizeTopic(topic: string): string {
  const stripped = stripParenthetical(topic)
  const alias = TOPIC_ALIAS_MAP[stripped.toLowerCase()]
  return alias ?? stripped
}

export function isPlaceholderTopic(topic: string): boolean {
  if (!topic) return false
  // Multi-word topics are never filtered by exact-match
  if (topic.trim().includes(' ')) return false
  const t = topic.trim().toLowerCase()
  return PLACEHOLDER_EXACT.has(t) || PURE_NUMERIC.test(t) || COORD_STYLE.test(t) || HEX_STRING.test(t)
}

export function aggregateProjectTopics(documents: { topics?: string[] }[]): string[] {
  const topicSet = new Set<string>()
  for (const doc of documents) {
    if (doc.topics) {
      for (const raw of doc.topics) {
        const normalized = normalizeTopic(raw)
        if (!isPlaceholderTopic(normalized)) topicSet.add(normalized)
      }
    }
  }
  return Array.from(topicSet).sort()
}
